import React, { Dispatch, SetStateAction, useRef, useState } from "react";
import { Button, Collapse } from "react-bootstrap";
import { ActionHandlerFunction, AdminUser, CollectionServerUser, IActionHandler, OrgInfo } from "@preveil-api";
import {
  Account, AdminErrorMessages, AdminSuccessMessages, AdminUIActionTypes, CSV_COLUMNS, GlobalErrorMessages, Message, MessageAnchors,
  MessageHandlerDisplayType, OrgUserRoles, useAddOrganizationMembersMutation, useAppDispatch, useCsvDataValidation, useOrgSubsumeMemberMutation,
  usePostUsersFindMutation, UserInviteMap, UserValidationState, FileTemplates, FileUploadTypes
} from "src/common";
import { DraggableContainer, Icon } from "src/components";
import { CSVInvitesDisplay } from "..";
import { uiActions } from "src/store";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  org_user_ids: string[];
  local_users_accounts_map: Map<string, Account> | undefined;
  org_info: OrgInfo;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  handleAction: ActionHandlerFunction;
}

function AddMultipleUsersComponent(props: AllProps) {
  const { current_account, org_user_ids, local_users_accounts_map, org_info, handleAction, setShowModal } = props;
  const dispatch = useAppDispatch();
  const [csv_data_hash, setCsvDataHash] = useState<UserInviteMap>({});
  const { valid_addresses, invalid_addresses, duplicate_addresses, is_data_drop, handleValidateData, resetData } = useCsvDataValidation(org_user_ids);
  const { validSubsumeAccountInvites, validExternalAccountInvites } = separateSubsumeAndExternalAccounts(valid_addresses);
  const [open_collapse, setOpenCollapse] = useState<boolean>(false);
  const csv_rows_with_missing_fields = useRef<string[]>([]);

  const [findUsers] = usePostUsersFindMutation();
  const [addOrganizationMembers] = useAddOrganizationMembersMutation();
  const [orgSubsumeMember] = useOrgSubsumeMemberMutation();

  const AddMultipleUsersRequests = {
    handleCsvData: (csvFile: any) => {
      handleCsvFileData(csvFile);
    },
    handleReset: () => {
      resetData();
    },
    handlePageError: (params: { message: string, stack?: any }) => {
      const { message, stack } = params;
      dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.toastr), stack));
    }
  };

  function handlePageActions(actionObj: IActionHandler) {
    const callback = `handle${actionObj.actionType}`;
    if ((AddMultipleUsersRequests as any)[callback] instanceof Function) {
      (AddMultipleUsersRequests as any)[callback](actionObj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, actionObj.actionType);
      AddMultipleUsersRequests.handlePageError({ message, stack: actionObj });
    }
  }

  function submitCSVData() {
    // Note: we could probably saved in state the validSubsumeAccountInvites and validExternalAccountInvites
    // and there would be no need to iterate over the csv_data_hash to get the user data again.
    // but this is probably easier and less costly than updating state every time invites are changed or removed.
    const externalAccountsCollection: AdminUser[] = [];
    const subsumeAccountsCollection: AdminUser[] = [];
    valid_addresses.forEach((address: string) => {
      const { first_name, last_name, email, department } = csv_data_hash[address];
      const user_invite = { display_name: `${first_name} ${last_name}`, user_id: email, department, devices: [], status: "pending", role: OrgUserRoles.standard };
      if (local_users_accounts_map?.has(email)) {
        subsumeAccountsCollection.push(user_invite);
      } else {
        externalAccountsCollection.push(user_invite);
      }
    });
    sendInvitesFromCsv(subsumeAccountsCollection, externalAccountsCollection);
  }

  // Description: Handles invites request from CSV file data, for both subsume and external accounts.
  async function sendInvitesFromCsv(subsumeAccounts: AdminUser[], externalAccounts: AdminUser[]) {
    if (subsumeAccounts.length >= 1 || externalAccounts.length >= 1) {
      try {
        // if there are any external accounts in the csv file, we need to call a different API.
        if (externalAccounts.length >= 1 && subsumeAccounts.length >= 1) {
          await sendUsersInvite(externalAccounts);
        } else if (externalAccounts.length >= 1 && subsumeAccounts.length === 0) {
          await sendUsersInvite(externalAccounts).then(() => {
            const successMessage = AdminSuccessMessages.success_invite_user.replace(MessageAnchors.message_content, `${externalAccounts.length} users`);
            dispatch(uiActions.handleSetMessage(new Message(successMessage)));
            reset();
          });
        }

        if (subsumeAccounts.length >= 1) {
          await Promise.all(subsumeAccounts.map((subsumeAccount) => sendSubsumeInvite(subsumeAccount))).then(() => {
            const totalInvites = subsumeAccounts.length + externalAccounts.length;
            const successMessage = AdminSuccessMessages.success_invite_user.replace(MessageAnchors.message_content, `${totalInvites} users`);
            reset();
            dispatch(uiActions.handleSetMessage(new Message(successMessage)));
          });
        }

      } catch (error) {
        const error_message = AdminErrorMessages.error_invite_request_csv_users;
        dispatch(uiActions.handleRequestErrors(new Message(error_message, MessageHandlerDisplayType.toastr), error));
      }
    }
  }

  function reset() {
    resetData();
    handleAction({ actionType: AdminUIActionTypes.GetRefreshRequest });
  }

  // Description: API for sending invites to external users.
  async function sendUsersInvite(users: AdminUser[]) {
    await addOrganizationMembers({
      orgId: org_info.org_id,
      userId: current_account.user_id,
      users
    })
      .unwrap()
      .catch((error) => {
        const error_message = AdminErrorMessages.error_invite_user_to_org.replace(MessageAnchors.user_id, `${users.length === 1 ? users[0].display_name : `${users.length} users`}`);
        dispatch(uiActions.handleRequestErrors(new Message(error_message, MessageHandlerDisplayType.toastr), error));
      });
  }

  async function sendSubsumeInvite(user: AdminUser) {
    await orgSubsumeMember({
      orgId: org_info.org_id,
      userId: current_account.user_id,
      subsumeUserId: user.user_id,
      department: user.department
    })
      .unwrap()
      .catch((error) => {
        const error_message = AdminErrorMessages.error_invite_user_to_org.replace(MessageAnchors.user_id, `${user.user_id}`);
        dispatch(uiActions.handleRequestErrors(new Message(error_message, MessageHandlerDisplayType.toastr), error));
      });
  }

  // Description: After the CSV data is validated, we will split the valid addresses into subsume and external accounts.
  function separateSubsumeAndExternalAccounts(usersInvites: string[]) {
    if (usersInvites.length === 0) return { validSubsumeAccountInvites: [], validExternalAccountInvites: [] };
    // We are partitioning the valid addresses into subsume and external accounts.
    const [subsumeCollection, collection] = _.partition(usersInvites, (address: string) => local_users_accounts_map?.has(address));
    return {
      validSubsumeAccountInvites: subsumeCollection,
      validExternalAccountInvites: collection
    };
  }

  // Description: Handling the CSV data after it's been parsed.
  async function handleCsvFileData(csvFile: any) {
    if (csvFile?.errors && csvFile?.errors.length >= 1) {
      // Papaparse returns some errors when the CSV formatting is not correct
      // or the data is missing a delimiter in each row, but that doesn't mean the data will not be parse.
      // so we are just logging those errors.
      const message = AdminErrorMessages.error_csv_upload_format;
      dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), csvFile.errors));
    }
    const { data, meta } = csvFile;
    const { fields } = meta;
    const isValidColumns = fields.length === [...Object.values(CSV_COLUMNS)].length && fields.every((field: "First Name" | "Last Name" | "Email Address" | "Department") => ([...Object.values(CSV_COLUMNS)]).includes(field));
    if (!isValidColumns || data.length === 0) {
      if (data.length === 0) {
        const message = AdminErrorMessages.error_csv_empty_file;
        dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), data));
      } else if (!isValidColumns) {
        const message = AdminErrorMessages.error_csv_wrong_heading;
        dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), meta.fields));
      }
    } else {
      // Description: We only need the email address/user_id to validate each row data of the CSV.
      // And also keeping a reference (inside a ref) of the csv data to be used later.
      const userAddressesFromCsv: string[] = [];
      const externalAccountsCollection: any = [];
      const csv_data_hash: UserInviteMap = {};
      csv_rows_with_missing_fields.current = [];
      data.forEach((row: any, index: string) => {
        const address = row[CSV_COLUMNS.email] || "";
        const department = row[CSV_COLUMNS.department] || "";
        const first_name = row[CSV_COLUMNS.first_name] || "";
        const last_name = row[CSV_COLUMNS.last_name] || "";
        // Note: We'll consider a row valid, if the email and department are present and at least one of first-name and last-name
        const isValidRow = !!address && !!department && (!!first_name || !!last_name);
        // Note: We are using the email as the id for the csv data hash, if the row is not valid we'll generate a unique id.
        // This is to keep the reference of the csv data to be used later.
        // All the invalid rows will fail the validation inside useCsvDataValidation and will be stored in the invalid_addresses array.
        const id = isValidRow ? address : _.uniqueId(`row_${index}`);
        userAddressesFromCsv.push(id);
        !local_users_accounts_map?.has(address) && externalAccountsCollection.push(address);
        csv_data_hash[id] = { first_name, last_name, email: address, department };
      });
      setCsvDataHash(csv_data_hash); // keeping the reference of the csv data in state since we are going to pass it down to other components.
      if (externalAccountsCollection.length > 0) {
        try {
          // if there are accounts that are not in the local_users_accounts_map, we need to find them and see if they are express subsume accounts.
          await findCsvUsers(externalAccountsCollection);
        } catch (error) {
          const message = AdminErrorMessages.error_finding_user_accounts_from_csv;
          dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), error));
        }
      }
      handleValidateData(userAddressesFromCsv);
    }
  };

  // Description: After getting the response from getUser
  // if there are any express subsume accounts, we will add them to the local_users_accounts_map.
  async function findCsvUsers(addressesFromCsv: string[]) {
    const result = await getUser(addressesFromCsv);
    if (!!result) {
      const { users } = result;
      if (!!users && users.length > 0) {
        users.forEach((user: CollectionServerUser) => {
          const { user_id } = user;
          local_users_accounts_map?.set(user_id, user as any);
        });
      }
    }
  };

  // Description: Get the user data from the API.
  // We are using the findUsers API to see if the user is an express subsume account.
  async function getUser(user_ids: string[]) {
    if (current_account && user_ids.length > 0) {
      const users = user_ids.map((userId) => {
        return { user_id: userId, key_version: -1 };
      });
      return await findUsers({
        account_ids: Account.getAccountIdentifiers(current_account),
        body: { spec: users }
      })
        .unwrap()
        .then((response) => {
          const { users, errors } = response;
          return { users, errors };
        })
        .catch((error) => {
          const message = AdminErrorMessages.error_finding_user_accounts_from_csv;
          dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), error));
        });
    }
  }

  // Description: Handling the delete action for the csv addresses
  function handleDeleteAction(id?: string) {
    if (!!id) {
      const data = [...valid_addresses, ...invalid_addresses, ...duplicate_addresses].filter((address: string) => address !== id);
      handleValidateData(data);
    }
  }

  return <>
    {!is_data_drop ? <>
      <p>
        Download and use the upload template to accurately add multiple users at once
        <a
          href={FileTemplates.multiple_users}
          target="_blank"
          rel="noreferrer">
          <Icon className="ficon-download small" />
          Preveil-User-Upload-Template.csv
        </a>
      </p>
      <DraggableContainer handleAction={handlePageActions} file_upload_type={FileUploadTypes.CSV} />
    </> : <>
      {validSubsumeAccountInvites.length > 0 &&
        <CSVInvitesDisplay
          csv_data_hash={csv_data_hash}
          validation_state={UserValidationState.valid_subsume}
          collection={validSubsumeAccountInvites}
          deleteFunction={handleDeleteAction}
        />
      }
      {validExternalAccountInvites.length > 0 &&
        <CSVInvitesDisplay
          csv_data_hash={csv_data_hash}
          validation_state={UserValidationState.valid_external}
          collection={validExternalAccountInvites}
          deleteFunction={handleDeleteAction}
        />
      }
      {
        invalid_addresses.length > 0 || duplicate_addresses.length > 0 &&
        <div className={`invalid-card ${(validSubsumeAccountInvites.length > 0 || validExternalAccountInvites.length > 0) ? "show-divider-line" : ""}`}>
          <header>
            <h3 className="mb-2">
              Invites that will <i className="emphasis">not</i> be sent
            </h3>
            <Button variant="icon" onClick={() => setOpenCollapse(!open_collapse)} aria-controls="collapse-invalid-area">
              <Icon className={`${open_collapse ? "ficon-chevron-up" : "ficon-chevron-down"}`} />
            </Button>
          </header>
          <Collapse in={open_collapse}>
            <div id="collapse-invalid-area">
              <div className="pt-2 pb-0">
                {duplicate_addresses.length >= 1 &&
                  <CSVInvitesDisplay
                    csv_data_hash={csv_data_hash}
                    validation_state={UserValidationState.duplicate}
                    collection={duplicate_addresses}
                    deleteFunction={handleDeleteAction}
                  />
                }
                {invalid_addresses.length >= 1 &&
                  <CSVInvitesDisplay
                    csv_data_hash={csv_data_hash}
                    validation_state={UserValidationState.invalid}
                    collection={invalid_addresses}
                    deleteFunction={handleDeleteAction}
                  />
                }
              </div>
            </div>
          </Collapse>
        </div>
      }
    </>
    }
    <div className="btn-panel mt-4">
      <Button onClick={submitCSVData}>Add/Create Accounts</Button>
      <Button variant="no-outline-primary" onClick={() => setShowModal(false)}>Cancel</Button>
    </div>
  </>;
}

export default React.memo(AddMultipleUsersComponent);